import { Loader2 } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { Button, EmptyState, formatKr } from "../ui";
import { Modal } from "./Modal";

type Member = {
	productId: number;
	storeId: string;
	title: string;
	ean: string | null;
	priceMinor: number | null;
	groupKey: string;
};

type Candidate = {
	signature: string;
	via: "title" | "attributes";
	members: Member[];
	verdict: { ok: true } | { ok: false; reason: string };
};

type Plan = { toRead: number; alreadyRead: number; model: string };

type Progress = {
	extracted: number;
	total: number;
	failedChunks: number;
	cost: number | null;
	lastError: string | null;
};

type Phase =
	| { kind: "planning" }
	| { kind: "plan"; plan: Plan }
	| { kind: "reading"; plan: Plan; progress: Progress | null }
	| { kind: "results"; candidates: Candidate[] | null }
	| { kind: "error"; message: string; needsModel?: boolean };

async function post<T>(path: string, body: unknown): Promise<T> {
	const res = await fetch(path, {
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(body),
	});
	const data = (await res.json().catch(() => ({}))) as T & { error?: string };
	if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
	return data;
}

const REASON_WORDS: Record<string, string> = {
	"more than one EAN": "they have different EANs",
	"prices too far apart": "the prices are too far apart",
	"title too generic": "the title is too generic to trust alone",
	"split out by hand": "one was split out by hand",
};

/**
 * AI matching for exactly the items on screen: read the listings of those
 * items that haven't been read, then list every proposed merge touching
 * them. Proposals that pass auto-merge's checks start ticked; the rest are
 * shown unticked with the reason, and nothing merges until you say so.
 */
export function FindMatchesModal({
	open,
	onClose,
	groupKeys,
	storeNames,
	onMerged,
}: {
	open: boolean;
	onClose: () => void;
	groupKeys: string[];
	storeNames: Map<string, string>;
	onMerged: () => void;
}) {
	const [phase, setPhase] = useState<Phase>({ kind: "planning" });
	const [ticked, setTicked] = useState<Set<string>>(new Set());
	const [busy, setBusy] = useState(false);
	const [note, setNote] = useState<string | null>(null);
	const [done, setDone] = useState<string | null>(null);
	const keysRef = useRef(groupKeys);
	keysRef.current = groupKeys;

	const loadResults = useCallback(async () => {
		setPhase({ kind: "results", candidates: null });
		try {
			const { candidates } = await post<{ candidates: Candidate[] }>("/api/grouping/scan/results", {
				groupKeys: keysRef.current,
			});
			setTicked(new Set(candidates.filter((c) => c.verdict.ok).map((c) => c.signature)));
			setPhase({ kind: "results", candidates });
		} catch (e) {
			setPhase({ kind: "error", message: (e as Error).message });
		}
	}, []);

	// A fresh plan every time the dialog opens, for whatever is on screen then.
	useEffect(() => {
		if (!open) return;
		setNote(null);
		setDone(null);
		setPhase({ kind: "planning" });
		post<Plan>("/api/grouping/scan", { groupKeys: keysRef.current })
			.then((plan) => setPhase({ kind: "plan", plan }))
			.catch((e: Error) => setPhase({ kind: "error", message: e.message, needsModel: e.message.includes("model") }));
	}, [open]);

	// While reading, follow the run; when it ends, show what it found.
	const reading = phase.kind === "reading";
	useEffect(() => {
		if (!reading) return;
		let stopped = false;
		const tick = async () => {
			const s = (await fetch("/api/grouping/extract").then((r) => r.json())) as Progress & {
				running: boolean;
			};
			if (stopped) return;
			if (!s.running) {
				// Matches are still worth showing when reading failed — the title
				// rule needs no AI — but say so, or a failed run looks like "found
				// nothing new".
				if (s.failedChunks > 0 && s.extracted === 0) {
					setNote(`Reading failed (${s.lastError ?? "unknown error"}), so only identical titles are shown.`);
				} else if (s.failedChunks > 0) {
					setNote(`Some listings couldn't be read (${s.lastError ?? "unknown error"}); run it again to retry them.`);
				}
				void loadResults();
				return;
			}
			setPhase((p) => (p.kind === "reading" ? { ...p, progress: s } : p));
			setTimeout(() => void tick(), 1000);
		};
		void tick();
		return () => {
			stopped = true;
		};
	}, [reading, loadResults]);

	const start = async (plan: Plan) => {
		if (plan.toRead === 0) {
			void loadResults();
			return;
		}
		try {
			await post("/api/grouping/scan", { groupKeys: keysRef.current, start: true });
			setPhase({ kind: "reading", plan, progress: null });
		} catch (e) {
			setPhase({ kind: "error", message: (e as Error).message });
		}
	};

	const mergeTicked = async (candidates: Candidate[]) => {
		setBusy(true);
		let merged = 0;
		try {
			for (const c of candidates) {
				if (!ticked.has(c.signature)) continue;
				await post("/api/grouping/merge", { productIds: c.members.map((m) => m.productId) });
				merged += 1;
			}
		} catch (e) {
			setDone(`Stopped after ${merged}: ${(e as Error).message}`);
		} finally {
			setBusy(false);
		}
		if (merged > 0) {
			setDone((d) => d ?? `Merged ${merged}.`);
			onMerged();
			void loadResults();
		}
	};

	const notSame = async (c: Candidate) => {
		await post("/api/grouping/reject", { signature: c.signature }).catch(() => {});
		setPhase((p) =>
			p.kind === "results" && p.candidates
				? { ...p, candidates: p.candidates.filter((x) => x.signature !== c.signature) }
				: p,
		);
	};

	const storeName = (id: string) => storeNames.get(id) ?? id;
	const items = `${groupKeys.length.toLocaleString()} item${groupKeys.length === 1 ? "" : "s"}`;

	let body: ReactNode;
	let footer: ReactNode = null;

	if (phase.kind === "planning") {
		body = <p className="px-4 py-4 text-[13px] text-muted">Working out what to read…</p>;
	} else if (phase.kind === "error") {
		body = (
			<div className="px-4 py-4 text-[13px]">
				{phase.needsModel ? (
					<p className="text-ink">
						AI matching needs an OpenRouter key and model.{" "}
						<a href="#/manage/settings" className="underline" onClick={onClose}>
							Set them in Settings
						</a>
					</p>
				) : (
					<p className="text-fault">{phase.message}</p>
				)}
			</div>
		);
	} else if (phase.kind === "plan") {
		const { plan } = phase;
		body = (
			<div className="px-4 py-4 text-[13px] text-ink">
				<p>Looks for matches for the {items} shown, across every store.</p>
				<p className="mt-2 text-muted">
					{plan.toRead === 0
						? `All ${plan.alreadyRead} of their listings are already read, so this only looks up matches.`
						: `${plan.toRead.toLocaleString()} listing${plan.toRead === 1 ? "" : "s"} to read with ${plan.model}${
								plan.alreadyRead > 0 ? `, ${plan.alreadyRead.toLocaleString()} already read` : ""
							}. Nothing merges until you choose.`}
				</p>
			</div>
		);
		footer = (
			<>
				<Button variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				<Button variant="primary" onClick={() => void start(plan)}>
					{plan.toRead === 0 ? "Find matches" : `Read ${plan.toRead.toLocaleString()} and find matches`}
				</Button>
			</>
		);
	} else if (phase.kind === "reading") {
		const p = phase.progress;
		const pct = p && p.total > 0 ? Math.round((p.extracted / p.total) * 100) : 0;
		body = (
			<div className="px-4 py-4 text-[13px]">
				<p className="flex items-center gap-2 text-ink">
					<Loader2 className="size-4 animate-spin" aria-hidden />
					Reading {p ? `${p.extracted.toLocaleString()} of ${p.total.toLocaleString()}` : "listings"}…
				</p>
				<div
					className="mt-3 h-1.5 overflow-hidden rounded-full bg-hairline"
					role="progressbar"
					aria-valuenow={pct}
					aria-valuemin={0}
					aria-valuemax={100}
				>
					<div className="h-full bg-live transition-[width]" style={{ width: `${pct}%` }} />
				</div>
				<p className="mt-2 text-xs text-muted">
					{p?.failedChunks ? `${p.failedChunks} chunk(s) failed and will be retried next time. ` : ""}
					{p?.cost != null ? `$${p.cost.toFixed(4)} so far. ` : ""}
					You can close this; the reading carries on.
				</p>
			</div>
		);
		footer = (
			<Button variant="secondary" onClick={() => void post("/api/grouping/extract/cancel", {}).catch(() => {})}>
				Stop reading
			</Button>
		);
	} else {
		const { candidates } = phase;
		if (candidates == null) {
			body = <p className="px-4 py-4 text-[13px] text-muted">Looking up matches…</p>;
		} else if (candidates.length === 0) {
			body = (
				<div className="p-5">
					<EmptyState
						title="No matches found for these items."
						description="Everything shown is either grouped already or has no listing elsewhere that reads the same."
					/>
					{(done ?? note) && <p className="mt-3 text-center text-xs text-muted">{done ?? note}</p>}
				</div>
			);
		} else {
			body = (
				<ul className="min-h-0 flex-1 divide-y divide-hairline overflow-y-auto">
					{note && <li className="px-4 py-2 text-xs text-needs">{note}</li>}
					{candidates.map((c) => (
						<li key={c.signature} className="flex gap-3 px-4 py-3">
							<input
								type="checkbox"
								aria-label="Merge these"
								checked={ticked.has(c.signature)}
								onChange={() =>
									setTicked((prev) => {
										const next = new Set(prev);
										if (next.has(c.signature)) next.delete(c.signature);
										else next.add(c.signature);
										return next;
									})
								}
								className="mt-1 size-4 shrink-0 accent-ink"
							/>
							<div className="min-w-0 flex-1">
								<p className="text-xs text-muted">
									{c.via === "title" ? "Same title" : "AI match"}
									{!c.verdict.ok && (
										<span className="text-needs">
											, left unticked because {REASON_WORDS[c.verdict.reason] ?? c.verdict.reason}
										</span>
									)}
								</p>
								<ul className="mt-1 flex flex-col gap-0.5">
									{c.members.map((m) => (
										<li key={m.productId} className="flex min-w-0 items-baseline gap-2 text-[13px]">
											<span className="w-20 shrink-0 truncate text-xs text-muted">{storeName(m.storeId)}</span>
											<a
												href={`#/items/${m.productId}`}
												target="_blank"
												rel="noreferrer"
												className="min-w-0 flex-1 truncate text-ink hover:underline"
											>
												{m.title}
											</a>
											<span className="shrink-0 text-xs font-semibold font-tag text-muted">
												{formatKr(m.priceMinor)}
											</span>
										</li>
									))}
								</ul>
							</div>
							<button
								type="button"
								onClick={() => void notSame(c)}
								className="h-7 shrink-0 self-start rounded px-2 text-xs text-muted hover:bg-raised hover:text-ink"
							>
								Not the same
							</button>
						</li>
					))}
				</ul>
			);
			const n = candidates.filter((c) => ticked.has(c.signature)).length;
			footer = (
				<>
					<span className="mr-auto text-xs text-muted">{done ?? `${candidates.length} proposed, ${n} ticked`}</span>
					<Button variant="secondary" onClick={onClose}>
						Close
					</Button>
					<Button variant="primary" onClick={() => void mergeTicked(candidates)} disabled={busy || n === 0}>
						{busy ? "Merging…" : `Merge ${n}`}
					</Button>
				</>
			);
		}
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			title="Find matches with AI"
			subtitle={`For the ${items} shown`}
			footer={footer}
		>
			{body}
		</Modal>
	);
}
